/**
 * AtlasLightningModule — lightning you can SEE (fire realism R7, 2026-09-25).
 *
 * WeatherModule's storm flash fires `atlas:lightning-strike` on its rising edge (the ONE hook line there — the same
 * hash that sets the thunder's distance and bearing); this module turns it into:
 *   · a drawn BOLT for a cloud-to-ground strike (lib/vfx/lightning.ts: midpoint displacement + branches) written
 *     into ONE preallocated ribbon buffer — one interleaved vertex buffer, no geometry per bolt, the mesh hidden
 *     between strikes (a mesh's visibility is free; a LIGHT's is not);
 *   · the in-cloud glow for EVERY strike (most stay in the cloud): the engine cloud field's flash lobe
 *     (`CloudMeshField.setFlash`, reaching the far impostors too), centred on the strike at the cloud base;
 *   · one parked DirectionalLight created here at init (THE LIGHT-SET IDENTITY LAW: never toggled, parked at 0)
 *     aimed from the strike's bearing — the ground lights up FROM that side — while WeatherModule's old ambient
 *     flash drops to ~30 % (`lightningAmbientShare`).
 * The timing is the strokes' envelope: a dim leader, 3–4 return strokes inside ~200 ms, dark by 0.45 s.
 * Levers: `__lightning()` (strikes, the last kind, segments, the envelope), `__lightning().strike('cg' | 'ic')`.
 * Not the engine's `LightningEffect`: it adds a PointLight per bolt (the light-set law) and a TubeGeometry per bolt.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, attribute, varyingProperty, modelViewMatrix, cameraProjectionMatrix, screenSize, exp, max, normalize, mix,
} from 'three/tsl';
import type { App, IModule } from 'enginex';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { groundYIn } from '../lib/grounding.js';
import { buildBolt, strikeKind, strokeTimes, strokeEnvelope, FLASH_S, BOLT_MAX_SEGMENTS, setLightningAmbientShare, type BoltSeg } from '../lib/vfx/lightning.js';
// surfaces (2026-09-27): §4.3 the channel and its flash on the physical curve
import { EMITTER_NITS, EMITTER_DISPLAY_MAX, emitterScene, lightningLightScene } from '../lib/vfx/fire_rules.js';
import { exposureState, lightFrame } from '../lib/photometry.js';
import { fireSystem } from '../lib/vfx/fire_system.js';
/** The ribbon core's peak in its own units (the `* 30` below): the channel's held luminance divides by it. */
const CHANNEL_REF = 30;

/* eslint-disable @typescript-eslint/no-explicit-any */
type N = any;
const FLOATS = 10; // A.xyz, B.xyz, side, end, width, intensity
const PARK_Y = -10000;

export class AtlasLightningModule implements IModule {
  readonly name = 'AtlasLightning';
  private app!: App;
  private mesh: THREE.Mesh | null = null;
  private buf: Float32Array | null = null;
  private ib: THREE.InterleavedBuffer | null = null;
  private readonly uFlash = uniform(0);
  /** §4.3 (surfaces, 2026-09-27): the channel's scale — emitterScene(1e6 cd/m², held 3 stops over the key) / CHANNEL_REF. */
  private readonly uChannel = uniform(1);
  private light: THREE.DirectionalLight | null = null;
  private strike: { t: number; kind: 'cg' | 'ic'; times: number[]; peak: number; at: { x: number; y: number; z: number }; distM: number } | null = null;
  private last = 0;
  /** Probe lever: hold the first return stroke's peak this many seconds (a swiftshader frame is slower than a stroke). */
  private hold = 0;
  private stats = { strikes: 0, cg: 0, ic: 0, segments: 0, lastKind: '', lastKm: 0, envelope: 0, light: 0, cloudHook: false };

  init(app: App): void {
    this.app = app;
    const scene = app.world.scene;
    // The ribbon: one interleaved buffer (one vertex buffer on WebGPU), preallocated for the largest bolt.
    const n = BOLT_MAX_SEGMENTS;
    this.buf = new Float32Array(n * 4 * FLOATS);
    this.ib = new THREE.InterleavedBuffer(this.buf, FLOATS);
    this.ib.setUsage(THREE.DynamicDrawUsage);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.InterleavedBufferAttribute(this.ib, 3, 0)); // A (the bounding volume reads it)
    g.setAttribute('aB', new THREE.InterleavedBufferAttribute(this.ib, 3, 3));
    g.setAttribute('aParams', new THREE.InterleavedBufferAttribute(this.ib, 4, 6));
    const idx = new Uint16Array(n * 6);
    for (let i = 0; i < n; i++) { const v = i * 4, o = i * 6; idx[o] = v; idx[o + 1] = v + 1; idx[o + 2] = v + 2; idx[o + 3] = v + 1; idx[o + 4] = v + 3; idx[o + 5] = v + 2; }
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.setDrawRange(0, 0);
    const m = this.ribbonMaterial();
    const mesh = new THREE.Mesh(g, m);
    mesh.name = 'lightning-bolt';
    mesh.frustumCulled = false;
    mesh.renderOrder = 17;
    mesh.visible = false;
    scene.add(mesh);
    this.mesh = mesh;
    // The flash light: created now, parked, never toggled (the light-set identity law).
    const l = new THREE.DirectionalLight(0xcfdcff, 0);
    l.name = 'lightning-flash';
    l.castShadow = false;
    l.position.set(0, PARK_Y, 0);
    l.target.position.set(0, PARK_Y - 1, 0);
    scene.add(l, l.target);
    this.light = l;
    // The drawn bolt and the directional flash carry the strike now: the ambient flash keeps ~30 %.
    setLightningAmbientShare(0.3);
    bus.on('atlas:lightning-strike', (s) => this.onStrike(s.bearing, s.km, s.h));
    bus.on('atlas:world-cleared', () => this.clear());
    app.world.addSystem(makeSystem('AtlasLightning', Phase.RenderSub, 23, () => this.tick()));
    (window as unknown as Record<string, unknown>).__lightning = () => ({
      ...this.stats, live: !!this.strike,
      strike: (kind: 'cg' | 'ic' = 'cg', km = 0.9, bearing = atlas.localYaw + Math.PI, holdS = 0) => { this.onStrike(bearing, km, kind === 'cg' ? 0.02 : 0.1); if (this.strike && holdS > 0) this.hold = holdS; return this.stats.lastKind; },
    });
  }

  /** The ribbon: each segment a camera-facing strip in VIEW space, ≥ 2.5 px wide; a white HDR core, a blue glow. */
  private ribbonMaterial(): THREE.NodeMaterial {
    const aB = attribute('aB', 'vec3'), aP = attribute('aParams', 'vec4');
    const vSide = varyingProperty('float', 'vBoltSide');
    const vI = varyingProperty('float', 'vBoltI');
    const m = new THREE.NodeMaterial();
    m.name = 'lightning-ribbon';
    m.transparent = true;
    m.depthWrite = false;
    m.fog = false;
    m.side = THREE.DoubleSide;
    m.blending = THREE.CustomBlending;
    m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneMinusSrcAlphaFactor;
    m.blendSrcAlpha = THREE.OneFactor; m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
    m.vertexNode = Fn(() => {
      const A = attribute('position', 'vec3');
      const va = modelViewMatrix.mul(vec4(A, 1)), vb = modelViewMatrix.mul(vec4(aB, 1));
      const p = mix(va, vb, aP.y);
      const d = vb.xy.sub(va.xy);
      const dn = normalize(d.add(vec2(1e-6, 0)));
      const perp = vec2(dn.y.negate(), dn.x);
      // The width floor: 2.5 px at this depth (metres per pixel = 2·z / (P₁₁ · H)).
      const z = p.z.negate().max(0.1);
      const mpp = z.mul(2).div((cameraProjectionMatrix as N).element(1).y.mul(screenSize.y));
      const w = max(aP.z, mpp.mul(2.5));
      vSide.assign(aP.x);
      vI.assign(aP.w);
      const q = vec4(p.xy.add(perp.mul(aP.x).mul(w)), p.z, float(1));
      return cameraProjectionMatrix.mul(q);
    })();
    m.colorNode = Fn(() => {
      const s = vSide.abs();
      const core = exp(s.mul(s).mul(-22)).mul(30);
      const glow = exp(s.mul(s).mul(-2.2)).mul(2.6);
      const rgb = vec3(1, 1, 1).mul(core).add(vec3(0.35, 0.5, 1.0).mul(glow));
      return vec4(rgb.mul(vI).mul(this.uFlash).mul(this.uChannel), 0);
    })();
    markMrtOverlay(m);
    return m;
  }

  private ground(x: number, z: number): number { return atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : 0; }

  private onStrike(bearing: number, km: number, h: number): void {
    if (atlas.spaceFlight || atlas.currentWorldId === null) return;
    const kind = strikeKind(h);
    const d = Math.max(250, km * 1000);
    const x = atlas.localX + Math.sin(bearing) * d, z = atlas.localZ + Math.cos(bearing) * d;
    const gy = this.ground(x, z);
    const top = Math.max(gy + 520, 1050);
    const seed = Math.floor(h * 1e6) ^ this.stats.strikes * 7919;
    this.stats.strikes++;
    this.stats.lastKind = kind; this.stats.lastKm = +km.toFixed(2);
    if (kind === 'cg') {
      this.stats.cg++;
      const segs = buildBolt(seed, { x: x + (Math.sin(seed) * 60), y: top, z: z + Math.cos(seed) * 60 }, { x, y: gy, z }, { width: 1.6 });
      this.writeBolt(segs);
    } else {
      this.stats.ic++;
      if (this.mesh) this.mesh.visible = false;
    }
    const falloff = 1 / (1 + 0.45 * km);
    this.strike = { t: 0, kind, times: strokeTimes(seed), peak: (kind === 'cg' ? 3.6 : 2.2) * falloff, at: { x, y: top, z }, distM: Math.hypot(d, top - atlas.localY) };
    // The directional flash comes FROM the strike (over the deck), toward the player.
    if (this.light) {
      this.light.position.set(atlas.localX + Math.sin(bearing) * 400, atlas.localY + 700, atlas.localZ + Math.cos(bearing) * 400);
      this.light.target.position.set(atlas.localX, atlas.localY, atlas.localZ);
      this.light.target.updateMatrixWorld();
    }
  }

  private writeBolt(segs: BoltSeg[]): void {
    const b = this.buf, mesh = this.mesh;
    if (!b || !mesh || !this.ib) return;
    const n = Math.min(segs.length, BOLT_MAX_SEGMENTS);
    for (let i = 0; i < n; i++) {
      const s = segs[i];
      const inten = s.i * (s.branch ? 0.55 : 1);
      const corner = [[-1, 0], [1, 0], [-1, 1], [1, 1]];
      for (let k = 0; k < 4; k++) {
        const o = (i * 4 + k) * FLOATS;
        b[o] = s.a.x; b[o + 1] = s.a.y; b[o + 2] = s.a.z;
        b[o + 3] = s.b.x; b[o + 4] = s.b.y; b[o + 5] = s.b.z;
        b[o + 6] = corner[k][0]; b[o + 7] = corner[k][1]; b[o + 8] = s.w; b[o + 9] = inten;
      }
    }
    this.ib.needsUpdate = true;
    (this.ib as unknown as { addUpdateRange?(start: number, count: number): void }).addUpdateRange?.(0, n * 4 * FLOATS);
    mesh.geometry.setDrawRange(0, n * 6);
    mesh.visible = true;
    this.stats.segments = n;
  }

  private cloudField(): { setFlash(p: { x: number; y: number; z: number }, i: number, r?: number): void } | null {
    const c = this.app.getModule('Clouds') as unknown as { meshField?: { setFlash?(p: { x: number; y: number; z: number }, i: number, r?: number): void } } | undefined;
    const mf = c?.meshField;
    return mf && typeof mf.setFlash === 'function' ? (mf as never) : null;
  }

  private tick(): void {
    const now = performance.now();
    const dt = this.last ? Math.min(0.1, (now - this.last) / 1000) : 1 / 60;
    this.last = now;
    const st = this.strike;
    if (!st) return;
    if (this.hold > 0) { this.hold -= dt; st.t = Math.min(st.t + dt, st.times[0] + 0.004); } else st.t += dt;
    const e = strokeEnvelope(st.t, st.times);
    this.stats.envelope = +e.toFixed(3);
    (this.uFlash as unknown as { value: number }).value = e;
    // §4.3 (surfaces, 2026-09-27): ON THE CURVE — the stroke's candela / d² capped 3 stops over the key's illuminance
    // (faint under a noon sun, a white flash over a moonlit field), and the channel held 3 stops over the key's display.
    // `peak` (fixed units) is the pre-curve A/B value (`__fire().physical(false)`).
    const onCurve = (fireSystem()?.physical ?? true) && lightFrame.valid;
    (this.uChannel as unknown as { value: number }).value = onCurve ? emitterScene(EMITTER_NITS.lightning, EMITTER_DISPLAY_MAX.lightning, exposureState) / CHANNEL_REF : 1;
    const li = (onCurve ? lightningLightScene(st.kind, st.distM, exposureState) : st.peak) * e;
    if (this.light) this.light.intensity = li;
    this.stats.light = +li.toPrecision(3);
    // The in-cloud lobe (the engine cloud field + its impostors): strongest for the strokes that stay in the cloud.
    const cf = this.cloudField();
    this.stats.cloudHook = !!cf;
    cf?.setFlash(st.at, e * (st.kind === 'ic' ? 9 : 6), st.kind === 'ic' ? 1100 : 800);
    if (st.t > FLASH_S) {
      this.strike = null;
      (this.uFlash as unknown as { value: number }).value = 0;
      if (this.mesh) this.mesh.visible = false;
      if (this.light) { this.light.intensity = 0; this.light.position.set(0, PARK_Y, 0); }
      cf?.setFlash(st.at, 0);
    }
  }

  private clear(): void {
    this.strike = null;
    if (this.mesh) this.mesh.visible = false;
    if (this.light) { this.light.intensity = 0; this.light.position.set(0, PARK_Y, 0); }
  }

  dispose(): void {
    this.clear();
    setLightningAmbientShare(1);
    this.mesh?.removeFromParent();
  }
}
